import i18n from "@/plugins/i18n";
import { QueryDosage } from "@/service/models/common/query-dosage/query-dosage";
import { RequestModelInterface } from "@/service/models/request-model-interface";
import { ResponseModelInterfaceStatic } from "@/service/models/response-model-interface";
import { DateFormatter } from "@/utilities/date-formatter";
import { staticImplements } from "@/utilities/static-implements";
import { XmlDocumentWrapper } from "@/utilities/xml-helpers/xml-document-wrapper";
import { XmlElementWrapper } from "@/utilities/xml-helpers/xml-element-wrapper";

@staticImplements<ResponseModelInterfaceStatic>()
export class AdjustmentDosageTimeRange implements RequestModelInterface
{
    public constructor(
        public start: Date,
        public end: Date,
        public dosages: QueryDosage[],
        public type: AdjustmentDosageTimeRange.Type = AdjustmentDosageTimeRange.Type.MANUAL,
    )
    {
    }

    public get lastDosage(): QueryDosage|null
    {
        return this.dosages[this.dosages.length - 1] ?? null;
    }

    /**
     * Givent any arbitrary date within this dosage, tells what is the
     * closest date of the next dose to be taken.
     * 
     * @param relativeTo        The date around which the closest dose should be searched
     */
    public getNextDoseDateFrom(relativeTo: Date): Date
    {
        if (!this.lastDosage) {
            throw new Error("AdjustmentDosageTimeRange has no dosages");
        }

        return this.lastDosage?.getInnerDosageChoice().getClosestNextDoseDate(this.start, relativeTo);
    }

    /**
     * Givent any arbitrary date within this dosage, tells what is the
     * closest date of the previous dose taken.
     * 
     * @param relativeTo        The date around which the closest dose should be searched
     */
    public getPreviousDoseDateFrom(relativeTo: Date): Date
    {
        if (!this.lastDosage) {
            throw new Error("AdjustmentDosageTimeRange has no dosages");
        }

        return this.lastDosage?.getInnerDosageChoice().getClosestPreviousDoseDate(this.start, relativeTo);
    }

    public static makeFromXml(elem: XmlElementWrapper): AdjustmentDosageTimeRange
    {
        return new this(
            elem.firstChildByTag("start").valueAsDate(),
            elem.firstChildByTag("end").valueAsDate(),
            elem.mapForEachChildTag("dosage", (elem) => QueryDosage.makeFromXml(elem)),
            AdjustmentDosageTimeRange.Type.SUGGESTED,
        );
    }

    public getXml(doc: XmlDocumentWrapper): XmlElementWrapper
    {
        const element = doc.createElement("dosageTimeRange");

        element.appendNewElement("start", this.start.toISOString());
        element.appendNewElement("end", this.end.toISOString());

        for (const dosage of this.dosages) {
            element.append(dosage.getXml(doc));
        }

        return element;
    }

    public getDescriptionString(): string
    {
        const dateFormat = new DateFormatter();
        const dosageChoice = this.lastDosage?.getInnerDosageChoice();

        let descriptionString = "";
        descriptionString += `From ${dateFormat.humanDate(this.start)} at ${dateFormat.humanTime(this.start)} : `;
        descriptionString += `${dosageChoice?.getDoseValueForDisplay()} [${dosageChoice?.getDoseUnitForDisplay()}] `;
        descriptionString += `every ${dosageChoice?.getIntervalForDisplay().toTextString()} `;
        descriptionString += `(${i18n.global.t('administrationRoute.' + dosageChoice?.getRouteForDisplay().administrationRoute)})`;

        return descriptionString;
    }
}

export namespace AdjustmentDosageTimeRange
{
    export enum Type {
        MANUAL = "manual",
        SUGGESTED = "suggested",
    }
}
